import { useMemo } from 'react'

import { ChartDonut } from '@gravity-ui/icons'
import * as Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { useTranslation } from 'react-i18next'

import { EmptyState } from '@/components/ui/EmptyState'
import { useAnalyticsStore } from '@/store/analytics.store'
import { useThemeStore } from '@/store/theme.store'

const CHART_COLORS = [
  '#5DBFFF', // light cyan blue
  '#A8ABFF', // light blue purple
  '#FF81B9', // light vibrant pink
  '#FF8289', // light vibrant red
  '#FF9A00', // vibrant orange
  '#DCBE00', // light vibrant yellow green
  '#72DB5A', // light vibrant green
  '#00D7FF', // light cyan
]

function getCountryColor(_country: string, index: number): string {
  return CHART_COLORS[index % CHART_COLORS.length]
}

export function AnalyticsDonutChart() {
  const { t } = useTranslation()
  const { reportItems, filters } = useAnalyticsStore()
  const isDark = useThemeStore(s => s.dark)

  const options = useMemo<Highcharts.Options>(() => {
    if (!filters.from || !filters.to || reportItems.length === 0) return {}

    const countryTotals = new Map<string, number>()

    reportItems.forEach(item => {
      const country = item.country || 'Unknown'
      const current = countryTotals.get(country) || 0
      countryTotals.set(country, current + (item.amountPLN || 0))
    })

    const sortedData = Array.from(countryTotals.entries())
      .map(([country, amount]) => ({ country, amount }))
      .sort((a, b) => b.amount - a.amount)

    const data = sortedData.map((d, i) => ({
      name: d.country,
      y: Number(d.amount.toFixed(2)),
      color: getCountryColor(d.country, i),
    }))

    const textColor = isDark ? '#e4e4e7' : '#27272a'

    return {
      chart: {
        type: 'pie',
        backgroundColor: 'transparent',
        style: { fontFamily: 'inherit' },
      },
      title: { text: '' },
      accessibility: { enabled: false },
      tooltip: {
        pointFormat: '<b>{point.y} PLN</b> ({point.percentage:.1f}%)',
      },
      plotOptions: {
        pie: {
          innerSize: '55%',
          borderWidth: 0,
          borderRadius: 4,
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b>: {point.y}',
            style: {
              color: textColor,
              textOutline: 'none',
              fontWeight: 'normal',
              fontSize: '12px',
            },
            connectorColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
          },
          showInLegend: true,
        },
      },
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
        itemStyle: {
          color: textColor,
          fontWeight: 'normal',
        },
        itemHoverStyle: {
          color: isDark ? '#ffffff' : '#000000',
        },
      },
      series: [
        {
          name: t('analytics.expenses_by_country', 'Expenses by Country'),
          data,
          type: 'pie',
        },
      ],
      credits: { enabled: false },
    }
  }, [reportItems, filters, isDark, t])

  if (reportItems.length === 0) {
    return (
      <div className="flex h-full min-h-[300px] items-center justify-center">
        <EmptyState
          icon={<ChartDonut className="size-8" />}
          title={t('analytics.no_expenses_period', { defaultValue: 'No expenses for this period' })} description={''}
        />
      </div>
    )
  }

  return (
    <div className="w-full h-full">
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        containerProps={{ style: { height: '100%', minHeight: '300px' } }}
      />
    </div>
  )
}
